import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Landmark, Link2, Loader2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Portfolio hub: connect/disconnect brokers + real holdings from any brokerage. */
export default function BrokerConnections() {
  const { authHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [holdings, setHoldings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/brokers/status`, { headers: authHeaders() });
      setStatus(r.data);
      if (r.data.snaptrade?.accounts?.length) {
        const h = await axios.get(`${API}/brokers/snaptrade/holdings`, { headers: authHeaders() });
        setHoldings(h.data.accounts);
      }
    } catch {
      setStatus(null);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const connectRobinhood = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/robinhood/connect`, {}, { headers: authHeaders() });
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Robinhood connection failed");
      setBusy(false);
    }
  };

  const connectSnapTrade = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/brokers/snaptrade/connect`, {}, { headers: authHeaders() });
      window.location.href = r.data.redirect_uri;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Brokerage connection failed");
      setBusy(false);
    }
  };

  const disconnectRobinhood = async () => {
    await axios.delete(`${API}/robinhood/disconnect`, { headers: authHeaders() });
    toast.success("Robinhood disconnected");
    load();
  };

  const disconnectSnapTrade = async (connectionId) => {
    try {
      await axios.delete(`${API}/brokers/snaptrade/connections/${connectionId}`, { headers: authHeaders() });
      toast.success("Brokerage disconnected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="broker-connections-section">
      <div className="flex items-center gap-2 mb-4">
        <Landmark className="w-5 h-5 text-[#00E0FF]" />
        <h2 className="text-lg font-bold text-white" style={{ fontFamily: "Unbounded" }}>BROKER CONNECTIONS</h2>
      </div>
      <Card className="glass-effect">
        <CardContent className="p-4 space-y-2">
          {status === null && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>}

          {status && (
            <>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5" data-testid="robinhood-connection-row">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${status.robinhood?.connected ? "bg-[#00FFA3]" : "bg-gray-600"}`} />
                  <div>
                    <div className="text-sm font-semibold text-white">Robinhood</div>
                    <div className="text-[10px] text-gray-500">Native · one-click trading{status.robinhood?.connected ? "" : " · not connected"}</div>
                  </div>
                </div>
                {status.robinhood?.connected ? (
                  <Button size="sm" variant="ghost" onClick={disconnectRobinhood} className="text-gray-500 hover:text-[#FF3B30] text-xs" data-testid="robinhood-disconnect-button">
                    <Unlink className="w-3 h-3 mr-1" /> Disconnect
                  </Button>
                ) : (
                  <Button size="sm" onClick={connectRobinhood} disabled={busy} className="bg-[#00FFA3]/15 text-[#00FFA3] hover:bg-[#00FFA3]/25 text-xs border border-[#00FFA3]/30" data-testid="robinhood-connect-row-button">
                    <Link2 className="w-3 h-3 mr-1" /> Connect
                  </Button>
                )}
              </div>

              {(status.snaptrade?.connections || []).map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5" data-testid={`snaptrade-connection-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${c.disabled ? "bg-[#FF3B30]" : "bg-[#00FFA3]"}`} />
                    <div>
                      <div className="text-sm font-semibold text-white">{c.brokerage}</div>
                      <div className="text-[10px] text-gray-500">
                        via SnapTrade · {c.type === "trade" ? "trading enabled" : "read-only"}{c.disabled ? " · connection broken" : ""}
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => disconnectSnapTrade(c.id)} className="text-gray-500 hover:text-[#FF3B30] text-xs" data-testid={`snaptrade-disconnect-${c.id}`}>
                    <Unlink className="w-3 h-3 mr-1" /> Disconnect
                  </Button>
                </div>
              ))}

              <Button onClick={connectSnapTrade} disabled={busy} variant="outline" className="w-full border-white/15 border-dashed text-gray-300 mt-1" data-testid="snaptrade-connect-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                Connect a brokerage — Fidelity, Schwab, Webull, IBKR + 30 more
              </Button>
            </>
          )}

          {holdings?.map((a) => (
            <div key={a.id} className="mt-3" data-testid={`snaptrade-holdings-${a.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
                {a.brokerage || a.institution_name} · {a.name || a.number}
                {a.total_value != null && <span className="text-gray-300 font-bold ml-2">${Number(a.total_value).toLocaleString()}</span>}
              </div>
              {(a.positions || []).length === 0 ? (
                <div className="text-xs text-gray-600 px-1">No positions</div>
              ) : (
                <div className="space-y-1">
                  {a.positions.map((p, i) => (
                    <div key={p.symbol || i} className="flex items-center justify-between text-xs rounded border border-white/5 bg-white/[0.015] px-2.5 py-1.5">
                      <span className="font-bold text-white mono">{p.symbol}</span>
                      <span className="text-gray-400 mono">{p.units} × ${Number(p.price || 0).toLocaleString()}</span>
                      <span className={`mono font-semibold ${Number(p.open_pnl || 0) >= 0 ? "text-[#00FFA3]" : "text-[#FF3B30]"}`}>
                        {Number(p.open_pnl || 0) >= 0 ? "+" : ""}{Number(p.open_pnl || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </motion.div>
  );
}
